// ─── 🚦 Prenotazioni e disdette, nella scheda del socio (fase B, 28/09/2026) ──
//
// Dal mockup «Prenotazione e disdetta», approvato: lo stato (libero / obbligo
// carta), le partite regolari «N su 5», lo storico delle infrazioni, e i due
// gesti della Segreteria — annullare un'infrazione e togliere l'obbligo — tutti
// e due col motivo, che resta scritto (§ 3.2, § 3.3). Prende il posto del
// lucchetto dei no-show (R16): una regola sola.
// Lo stato non si scrive: lo calcola il gestionale.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETICHETTE_INFRAZIONE,
  giornoPerEsteso,
  type Infrazione,
  type StatoDisdetteDelSocio,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

/** Nullo = togli l'obbligo; altrimenti l'infrazione da annullare. */
interface Gesto {
  infrazioneId: string | null;
  motivo: string;
}

type Apre = { cosa: 'annulla'; infrazione: Infrazione } | { cosa: 'togli' } | null;

export function DisdetteDelSocio({
  id,
  puòCorreggere,
}: {
  id: string;
  puòCorreggere: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [apre, setApre] = useState<Apre>(null);
  const [motivo, setMotivo] = useState('');
  const s = useQuery({
    queryKey: ['disdette-socio', id],
    queryFn: () => chiama<StatoDisdetteDelSocio>('GET', `/members/${id}/booking-status`),
  });
  const correggi = useMutation({
    meta: {
      fatto: (_dati: unknown, v: unknown) =>
        (v as Gesto).infrazioneId === null ? 'obbligo carta tolto' : 'infrazione annullata',
    },
    mutationFn: (v: Gesto) =>
      v.infrazioneId === null
        ? chiama<StatoDisdetteDelSocio>('POST', `/members/${id}/card-requirement/remove`, {
            reason: v.motivo,
          })
        : chiama<StatoDisdetteDelSocio>('POST', `/infractions/${v.infrazioneId}/annul`, { reason: v.motivo }),
    onSuccess: (dopo) => {
      qc.setQueryData(['disdette-socio', id], dopo);
      void qc.invalidateQueries({ queryKey: ['calendario'] });
      setApre(null);
      setMotivo('');
    },
  });

  if (!s.data) return null;
  const d = s.data;
  if (d.rules === 'spento' && d.infractions.length === 0) return null;
  const obbligo = d.status === 'obbligo_carta';

  return (
    <section className="riquadro disdette-socio">
      <h3>
        Prenotazioni e disdette{' '}
        <span className={`pill-caparra ${obbligo ? 'pill-attesa' : 'pill-ok'}`}>
          {obbligo ? 'Obbligo carta' : 'Libero'}
        </span>{' '}
        {d.rules === 'registro' && <span className="dal-bot">solo registro</span>}
      </h3>
      {obbligo ? (
        <dl className="piccolo">
          <dt>Partite regolari</dt>
          <dd>
            <span className="pallini" aria-hidden="true">
              {Array.from({ length: d.needed }, (_, i) => (
                <i key={i} className={i < d.regularGames ? 'pieno' : ''} />
              ))}
            </span>{' '}
            <strong>
              {d.regularGames} su {d.needed}
            </strong>
          </dd>
          <dt>Carta salvata</dt>
          <dd className="tenue">si salva quando c’è la carta a garanzia</dd>
        </dl>
      ) : (
        <p className="piccolo">Prenota senza carta.</p>
      )}

      {d.infractions.length > 0 && (
        <ul className="storico">
          {d.infractions.map((x) => (
            <li key={x.id} className={x.annulled ? 'annullata' : x.counted ? '' : 'registro'}>
              {x.annulled ? (
                <>
                  <s>
                    <strong>{ETICHETTE_INFRAZIONE[x.kind]}</strong> · {giornoPerEsteso(x.date)}, {x.time} ·{' '}
                    {x.courtName}
                  </s>
                  <br />
                  <span className="piccolo">
                    annullata il {new Date(x.annulled.at).toLocaleDateString('it-IT')}
                    {x.annulled.by ? ` da ${x.annulled.by}` : ''}: «{x.annulled.reason}»
                  </span>
                </>
              ) : (
                <>
                  <strong>{ETICHETTE_INFRAZIONE[x.kind]}</strong> · {giornoPerEsteso(x.date)}, {x.time} ·{' '}
                  {x.courtName} · {x.recordedBy ? `segnata da ${x.recordedBy}` : 'dal bot'}
                  {!x.counted && (
                    <>
                      <br />
                      <span className="piccolo">nel «solo registro»: non conta</span>
                    </>
                  )}
                  {puòCorreggere && apre === null && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="bordato piccolo"
                        onClick={() => setApre({ cosa: 'annulla', infrazione: x })}
                      >
                        ↩️ Annulla l’infrazione
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {d.removed && (
        <p className="piccolo tenue">
          Obbligo tolto il {new Date(d.removed.at).toLocaleDateString('it-IT')}
          {d.removed.by ? ` da ${d.removed.by}` : ''}: {d.removed.reason}
        </p>
      )}

      {puòCorreggere && obbligo && apre === null && (
        <button type="button" className="bordato" onClick={() => setApre({ cosa: 'togli' })}>
          🔓 Togli l’obbligo carta
        </button>
      )}

      {apre && (
        <div className="finestra-caparra">
          <label className="campo">
            <span>
              {apre.cosa === 'togli'
                ? 'Perché togli l’obbligo carta? (resta scritto)'
                : `Perché annulli l’infrazione del ${giornoPerEsteso(apre.infrazione.date)}? (per esempio: infortunio, malattia)`}
            </span>
            <input id="motivo-disdette" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </label>
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={motivo.trim().length < 3 || correggi.isPending}
              onClick={() =>
                correggi.mutate({
                  infrazioneId: apre.cosa === 'togli' ? null : apre.infrazione.id,
                  motivo: motivo.trim(),
                })
              }
            >
              {apre.cosa === 'togli' ? 'Togli l’obbligo' : 'Annulla l’infrazione'}
            </button>
            <button
              type="button"
              className="bordato"
              onClick={() => {
                setApre(null);
                setMotivo('');
              }}
            >
              Lascia stare
            </button>
          </div>
          <Guasto errore={correggi.error} />
        </div>
      )}
    </section>
  );
}
